<template>
    <div class="general-settings smr-panel" :class="{'is-saving': saving}">
        <el-dialog :visible.sync="dialogVisible" :show-close="false" @close="dialogCloseHandler" :top="dialogTopMargin">
            <div class="smr-panel__header smr-panel__header--big">
                <nav class="general-settings__switch">
                    <div class="general-settings__switch__link" :class="{'is-active-switch' : isSwitchActive(option.id)}"  @click="selectSwitch(option.id)"  v-for="option in switchOptions" :key="option.id">
                        <div class="general-settings__switch__link__inner h1">{{option.label}}</div>
                    </div>
                </nav>
                <div class="smr-panel__actions">
                    <div class="smr-panel__actions__item smr-panel__actions__close" @click="closeClickHandler">
                        <SmrTooltip content="Fenster schließen, Einstellungen verwerfen" position="bottom" :zIndex="true">
                            <icon><IconClose/></icon>
                        </SmrTooltip>    
                    </div>
                    <div class="smr-panel__actions__item smr-panel__actions__save" @click="saveClickHandler">
                        <Loader class="smr-panel__actions__item__loader" v-if="saving"/>
                        <SmrTooltip content="Einstellungen übernehmen" position="right" :zIndex="true">
                            <icon v-if="!saving"><IconCheck/></icon>
                        </SmrTooltip>    
                    </div>
                </div>
            </div>
            <div class="smr-panel__body smr-panel__body--outer">
                <div class="general-settings__item"  v-for="option in switchOptions" :key="option.id" :class="{'is-active-switch' : isSwitchActive(option.id)}">
                    <component
                        v-show="isSwitchActive(option.id)"
                        :is="getBodyComponentName(option.component)"
                        :options-id="option.id" @change="bodyComponentChangeHandler($event, option.id)" 
                        ref="options-panel"
                        @saving="savingHandler"
                        class="general-settings__item__inner"
                     />
                </div>
            </div>
            <div class="smr-panel__footer">
                <SmrButton class="smr-panel__footer__button" color="grey" @click.native="closeClickHandler">Abbrechen</SmrButton>
                <SmrButton class="smr-panel__footer__button" @click.native="saveClickHandler" :loading="saving">Übernehmen</SmrButton>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import eventBus from '@/utils/event-bus';
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { getPermanentData } from '@/utils/browser-storage';

import SmrTooltip from '@/components/SmrTooltip.vue'
import Icon from '@/components/Icon.vue'
import IconCheck from '@/components/icons/IconCheck.vue'
import IconClose from '@/components/icons/IconClose.vue'
import SmrButton from '@/components/SmrButton.vue'
import Loader from '@/components/Loader.vue'
import RouteSettings from '@/components/RouteSettings.vue'
import POISettings from '@/components/POISettings.vue'

export default {
    components: {
        SmrTooltip,
        Icon,
        IconCheck,
        IconClose,
        SmrButton,
        Loader,
        RouteSettings,
        POISettings
    },
    props: {
        initialSwitch: {
            default: false,
            type: [String, Boolean]
        },
        initialTab: {
            default: false,
            type: [String, Boolean]
        },
        initialData: {
            default: () => {},
            type: Object
        },
    },
    data() {
        return {
            saving: false,
            dialogVisible: false,
            activeSwitch: 'routes',
            switchOptions: [
                {
                    label: 'Routen',
                    id: 'routes',
                    component: 'RouteSettings',
                },
                {
                    label: 'Interessante Orte',
                    id: 'pois',
                    component: 'POISettings',
                },
            ]
        }
    },
    computed: {
        ...mapGetters(['mobile', 'savedRouteSettingsActive',]),
        dialogTopMargin() {
            return this.mobile ? '0' : '5vh';
        }
    },
    watch: {
        saving(value) {
            if(!value) {
                this.closeDialog();
            }
        }
    },
    created() {
        this.addEventListeners();
        this.retrieveSettingsFromBrowserStorage()
    },
    methods: {
        addEventListeners() {
            eventBus.$on('closeOptionsPanel', this.closeClickHandler);
            eventBus.$on('showGeneralSettings', this.showSettingsHandler);
        },
        showSettingsHandler(e) {
            if(e.switch) this.selectSwitch(e.switch)
            this.showDialog();
        },
        dialogCloseHandler(){
            // this.emitCloseEvent();
        },
        closeClickHandler() {
            this.closeDialog();
        },
        saveClickHandler() {
            trackEvent({ category: 'Route Planner', action: 'Options', label: 'Save' })
            trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: 'save', ga4Parameter2Name: 'tab', ga4Parameter2Value: 'general', ga4Event: 'route_planner_settings'})
            this.saveOptions();
        },
        savingHandler(e) {
            this.saving = e
        },
        showDialog() {
            this.dialogVisible = true;
        },
        closeDialog() {
            this.dialogVisible = false;
        },
        isSwitchActive(id) {
            return id === this.activeSwitch;
        },
        selectSwitch(id) {
            this.activeSwitch = id;

            // update views (slider)
            if(this.$refs['options-panel']) {
                this.$refs['options-panel'].forEach(panel => {
                    panel.updateView()
                })
            }
        },
        getBodyComponentName(component) {
            return component ? component : false;
        },
        bodyComponentChangeHandler(data, id) { // TODO -> check if not needed?
            this.$emit('settings-change', {data, id});
        },
        saveOptions() {
            this.$refs['options-panel'].forEach(panel => {
                panel.triggerSave()
            })
        },
        retrieveSettingsFromBrowserStorage() {
            // route settings
            if(!this.savedRouteSettingsActive) {
                const storedRouteSettings = getPermanentData('route-settings');
                if(storedRouteSettings) {
                    this.$store.dispatch('setRouteSettings', storedRouteSettings);
                }
            }

            // poi settings
            const storedPoiSettings = getPermanentData('poi-settings');
            if(storedPoiSettings) {
                this.$store.commit('setPoiSettings', storedPoiSettings);
            }

            // gas consumption settings
            const storedGasConsumptionSettings = getPermanentData('gas-consumption-settings')
            if(storedGasConsumptionSettings) {
                this.$store.commit('setGasConsumptionSettings', storedGasConsumptionSettings);
            }
        },
    }
}
</script>

<style lang="scss">
.general-settings {
    &__switch {
        display: flex;

        &__link {
            padding: 0 8px;
            overflow: hidden;
            color: $color-black-05;
            transition: color 0.2s;
            user-select: none;
            cursor: pointer;

            &.is-active-switch,
            .no-touch &:hover {
                color: $color-black;
            }

            &:first-child {
                padding-left: 0;
            }

            &__inner {
                position: relative;
                padding: 20px 0;
                height: 100%;

                .is-active-switch & {
                    &::after {
                        transform: none;
                    }
                }

                &::after {
                    content: '';
                    transition: transform 0.2s;
                    transform: translateY(100%);
                    position: absolute;
                    display: block;
                    background-color: $color-yellow;
                    height: 2px;
                    left: 0;
                    bottom: 0;
                    width: 100%;
                }
            }
        }
    }

    &__item {
         &.is-active-switch {
            height: 100%;
        }
        &__inner {
            height: 100%;
        }
    }
}
</style>